import * as PopperJS from '@popperjs/core';
import React, { useRef, useState } from 'react';
import useOnclickOutside from 'react-cool-onclickoutside';
import { usePopper } from 'react-popper';
import { LayoutProps, SpaceProps } from 'styled-system';

import { Container, Content } from './styles';

type RenderProps<T> = {
  closePopper: () => void;
  closesPopper: (fn: () => void) => () => void;
  extraData: T;
};

type PopperProps<T> = {
  referenceElement: Element | null;
  popperStyles: { [key: string]: React.CSSProperties };
  setPopperElement: (element: any) => void;
  closePopper: () => void;
  attributes: {
    [key: string]: {
      [key: string]: string;
    };
  };
  extraData: T;
  ref: React.RefObject<HTMLDivElement>;
};

type PropTypes<T> = {
  children: (props: RenderProps<T>) => React.ReactNode;
  popperProps: PopperProps<T>;
} & SpaceProps &
  LayoutProps &
  React.HTMLAttributes<HTMLDivElement>;

function createMenu<T>() {
  return {
    useMenu: (popperProps: Partial<PopperJS.Options> = {}) => {
      const [referenceElement, setReferenceElement] = useState<Element | null>(null);
      const [popperElement, setPopperElement] = useState(null);
      const { styles: popperStyles, attributes } = usePopper(referenceElement, popperElement, {
        placement: 'right',
        ...popperProps,
      });

      const ref = useRef<HTMLDivElement>(null);

      const closePopper = (): void => setReferenceElement(null);

      useOnclickOutside(ref, closePopper);

      const [extraData, setExtraData] = useState<T>();

      return {
        popperProps: {
          referenceElement,
          popperStyles,
          setPopperElement,
          closePopper,
          attributes,
          extraData,
          ref,
        },

        openMenuAt: (element: Element | null, data: T): void => {
          setExtraData(data);
          setReferenceElement(element);
        },
      };
    },

    Menu({
      children,
      popperProps: { referenceElement, popperStyles, setPopperElement, closePopper, attributes, extraData, ref },
      ...props
    }: PropTypes<T>): JSX.Element | null {
      return (
        referenceElement && (
          <Content ref={setPopperElement} style={popperStyles.popper} {...attributes.popper} {...props}>
            <Container ref={ref} flexDirection="column">
              {children({
                extraData,
                closesPopper: (callback) => (): void => {
                  closePopper();
                  callback();
                },
                closePopper,
              })}
            </Container>
          </Content>
        )
      );
    },
  };
}

export default createMenu;
